import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { roundUpToFactorOf } from 'utils/numbers';
import { pixelsToTimeFormat } from 'utils/time-helper';
import styles from './CursorOnTimelineIndicator.module.scss';

function useWindowMousePositionOnReference({
  ref,
}: {
  ref?: { current: HTMLElement | null | undefined };
}) {
  const [WindowMousePosition, setWindowMousePosition] = useState({
    actualOffsetY: 0,
  });

  useEffect(() => {
    const parent = ref?.current?.parentNode;
    const parentElement: HTMLElement | null = parent instanceof HTMLElement ? parent : null;

    function handleMouseMove(e: MouseEvent) {
      if (!parentElement) {
        return;
      }
      const { top } = parentElement.getBoundingClientRect();

      const { pageY } = e;
      const { scrollTop } = parentElement;

      setWindowMousePosition({
        actualOffsetY: scrollTop + pageY - top,
      });
    }

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [ref]);

  return WindowMousePosition;
}

interface Props {
  pixelsPerMinute: number;
  offsetLeftPx: number;
  offsetTopPx: number;
  timeFormat: 12 | 24;
  parentRef: {
    current: null | HTMLElement;
  };
  snapToMinutes: number;
  indicatorWidthPx: number;
}

export function CursorOnTimelineIndicator(props: Props) {
  const {
    pixelsPerMinute,
    timeFormat,
    parentRef,
    snapToMinutes,
    indicatorWidthPx,
    offsetLeftPx,
    offsetTopPx,
  } = props;
  const { actualOffsetY } = useWindowMousePositionOnReference({ ref: parentRef });
  // For timestamp calculations, also respect offsetTopPx -- cursor position is already accurate
  const roundedOffsetY = roundUpToFactorOf(
    actualOffsetY - offsetTopPx,
    snapToMinutes * pixelsPerMinute
  );
  const timestamp = pixelsToTimeFormat(roundedOffsetY, { pixelsPerMinute, timeFormat });

  return (
    <div
      className={classNames(styles.timeIndicator, 'cursorOnTimelineIndicator')}
      style={{ left: offsetLeftPx, top: actualOffsetY, width: indicatorWidthPx - offsetLeftPx }}
    >
      <div className={styles.timestamp} style={{ left: -offsetLeftPx }}>
        {timestamp}
      </div>
    </div>
  );
}
